import { useEffect, type ReactNode } from 'react'
import { ConsentBanner } from '@/components/consent/ConsentBanner'
import { StorefrontFooter } from '@/components/layout/StorefrontFooter'
import { StorefrontHeader } from '@/components/navigation/StorefrontHeader'
import type { StorefrontIdentity } from '@/features/clubs/storefront'
import { rememberStorefrontVisit } from '@/features/clubs/storefrontReturn'
import { storefrontAccentStyle } from '@/features/clubs/storefrontTheme'
import styles from './PublicShell.module.css'

type Props = {
  children: ReactNode
  /** The club this page belongs to, resolved by `useStorefrontClub` from the slug the
   *  public club read carries. Its presence IS the licence to render this shell. */
  club: StorefrontIdentity
}

/**
 * THE CLUB'S OWN SHELL (Club Storefront, brick 1) — the fourth chrome mode, beside
 * marketing, player and auth-minimal. Same skeleton as `PublicShell` (skip link, scan
 * lines, fixed header over a centred column, footer); the club's header and footer in
 * place of the platform's.
 *
 * The scan-line layer stays — it is the app's ambient tone, carried by every dark
 * surface here, and it says nothing about who made the software.
 *
 * THE CLUB'S ACCENT IS SET HERE AND NOWHERE ELSE (brick 3). Five token overrides on this
 * one element re-tint the header, the footer and the whole page inside it, because every
 * accent-derived token in `styles/tokens.css` resolves against the element it is used on.
 * `undefined` for a club that set no colour — or set one this build refuses — so the
 * ordinary Jarvis theme renders untouched rather than half-dressed.
 *
 * `ConsentBanner` STAYS, and this is not an oversight. It is the analytics consent gate,
 * a legal requirement that follows the data rather than the branding; a storefront that
 * dropped it would be a storefront that quietly stopped asking.
 *
 * AND THIS SHELL IS WHERE THE TAB LEARNS WHICH CLUB IT IS READING (2026-09-07), so that
 * a platform page can offer the way back — `features/clubs/storefrontReturn.ts`. The
 * note is written HERE and not on each link out, because this is the one component that
 * mounts only when a real club page is really on screen: every way out is then covered
 * at once, including the ones no link can be attached to. It lands while the storefront
 * is still being read, which is always before the click that leaves it.
 */
export function StorefrontShell({ children, club }: Props) {
  useEffect(() => {
    rememberStorefrontVisit(club.slug)
  }, [club.slug])
  return (
    <div className={styles.shell} style={storefrontAccentStyle(club.accent)}>
      <a href="#main-content" className={styles.skipLink}>
        Skip to content
      </a>
      <div className={styles.scanlines} aria-hidden="true" />
      <div className={styles.content}>
        <StorefrontHeader club={club} />
        <main id="main-content" className={`container ${styles.main}`}>
          {children}
        </main>
        <StorefrontFooter club={club} />
      </div>
      <ConsentBanner />
    </div>
  )
}
